import { useEffect } from "react";
import { SITE, FAQS } from "@/lib/site";

function upsertMeta(attr, key, content) {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function upsertJSONLD(id, obj) {
  let el = document.head.querySelector(`script[data-jsonld="${id}"]`);
  if (!el) {
    el = document.createElement("script");
    el.setAttribute("type", "application/ld+json");
    el.setAttribute("data-jsonld", id);
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(obj);
}

export function useSEO({ title, description, path = "/", keywords }) {
  useEffect(() => {
    const fullTitle = title
      ? `${title} | Dr. Anusha Mandava`
      : "Dr. Anusha Mandava | Radiation Oncologist in Hyderabad";
    document.title = fullTitle;

    upsertMeta("name", "description", description);
    upsertMeta("name", "keywords", keywords);
    upsertMeta("property", "og:title", fullTitle);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:type", "website");
    upsertMeta("property", "og:url", `${SITE.domain}${path}`);
    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", fullTitle);
    upsertMeta("name", "twitter:description", description);
    upsertMeta("name", "robots", "index, follow");
    upsertLink("canonical", `${SITE.domain}${path}`);
  }, [title, description, path, keywords]);
}

export function injectGlobalSchema() {
  const physician = {
    "@context": "https://schema.org",
    "@type": "Physician",
    name: "Dr. Anusha Mandava",
    medicalSpecialty: "RadiationOncologic",
    url: SITE.domain,
    telephone: SITE.phoneRaw,
    email: SITE.email,
    address: {
      "@type": "PostalAddress",
      addressLocality: "Hyderabad",
      addressRegion: "Telangana",
      addressCountry: "IN",
    },
    availableService: [
      "Breast Cancer Radiation Therapy",
      "Gynecological Cancer Radiation Therapy",
      "Head and Neck Cancer Radiation Therapy",
      "Brachytherapy",
      "IMRT", "VMAT", "IGRT", "SRS", "SBRT",
    ],
  };

  const clinic = {
    "@context": "https://schema.org",
    "@type": "MedicalClinic",
    name: "Dr. Anusha Mandava — Radiation Oncology",
    url: SITE.domain,
    telephone: SITE.phoneRaw,
    address: {
      "@type": "PostalAddress",
      addressLocality: "Hyderabad",
      addressRegion: "Telangana",
      addressCountry: "IN",
    },
  };

  const local = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    name: "Dr. Anusha Mandava — Radiation Oncology",
    url: SITE.domain,
    telephone: SITE.phoneRaw,
    priceRange: "$$",
    address: {
      "@type": "PostalAddress",
      addressLocality: "Hyderabad",
      addressRegion: "Telangana",
      addressCountry: "IN",
    },
    geo: {
      "@type": "GeoCoordinates",
      latitude: 17.385044,
      longitude: 78.486671,
    },
    openingHours: "Mo-Sa 10:00-18:00",
  };

  const faq = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQS.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  upsertJSONLD("physician", physician);
  upsertJSONLD("clinic", clinic);
  upsertJSONLD("local", local);
  upsertJSONLD("faq", faq);
}
